import * as Yup from 'yup'
import { TFunction } from 'i18next'
import { AdvertiserConfig, BudgetCapping, CostType, DailyBudgetType } from '../../../../../__generated__/graphql'
import { BudgetPlanFormValues, hasBudgetPlanChange, todayUtcDateOnly, toUtcDateOnly } from './budgetPlanForm.utils'

export type BudgetPlanSchemaOptions = {
  existingPlannedDates?: string[]
  todayUtc?: string
  currentCostType?: CostType
  t?: TFunction
}

const isEmptyOptional = (value: unknown): boolean => value == null || value === ''

const identityT = ((key: string) => key) as TFunction

const costLimitsForType = (
  config: AdvertiserConfig['adGroup'],
  costType: CostType | '' | undefined
): { min: number; max: number } | null => {
  switch (costType) {
    case CostType.Cpc:
      return { min: config.min.cpc, max: config.max.cpc }
    case CostType.Cpl:
      return { min: config.min.cpl, max: config.max.cpl }
    case CostType.Cpo:
      return { min: config.min.cpo, max: config.max.cpo }
    case CostType.Cpm:
      return { min: config.min.cpm, max: config.max.cpm }
    default:
      return null
  }
}

const numberMinMessage = (t: TFunction, min: number): string => t('commons.validation.numberMin', { min })
const numberMaxMessage = (t: TFunction, max: number): string => t('commons.validation.numberMax', { max })

export const budgetPlanSchema = (
  config: AdvertiserConfig['adGroup'],
  options: BudgetPlanSchemaOptions = {}
): Yup.ObjectSchema<BudgetPlanFormValues> => {
  const existingPlannedDates = options.existingPlannedDates ?? []
  const todayUtc = options.todayUtc ?? todayUtcDateOnly()
  const currentCostType = options.currentCostType
  const t = options.t ?? identityT

  return Yup.object({
    plannedDate: Yup.mixed<string | null>()
      .nullable()
      .required('commons.required')
      .test('valid-date', 'commons.required', (value) => toUtcDateOnly(value) != null)
      .test('future-date', 'adgroup.budgetAndDates.budgetPlans.dateMustBeFuture', (value) => {
        const dateOnly = toUtcDateOnly(value)
        return dateOnly != null && dateOnly > todayUtc
      })
      .test('duplicate-date', 'adgroup.budgetAndDates.budgetPlans.duplicateDate', (value) => {
        const dateOnly = toUtcDateOnly(value)
        return dateOnly == null || !existingPlannedDates.includes(dateOnly)
      }),
    dailyBudgetType: Yup.mixed<DailyBudgetType | ''>().oneOf([
      '',
      DailyBudgetType.Fixed,
      DailyBudgetType.Open,
      DailyBudgetType.Auto,
    ]),
    dailyBudget: Yup.number()
      .nullable()
      .transform((value, originalValue) => (isEmptyOptional(originalValue) ? null : value))
      .when('dailyBudgetType', {
        is: DailyBudgetType.Fixed,
        then: (schema) =>
          schema
            .required('commons.required')
            .min(config.min.dailyBudget, numberMinMessage(t, config.min.dailyBudget))
            .max(config.max.dailyBudget, numberMaxMessage(t, config.max.dailyBudget)),
        otherwise: (schema) =>
          schema
            .nullable()
            .min(config.min.dailyBudget, numberMinMessage(t, config.min.dailyBudget))
            .max(config.max.dailyBudget, numberMaxMessage(t, config.max.dailyBudget)),
      }),
    budgetCappingType: Yup.mixed<BudgetCapping | ''>().oneOf([
      '',
      BudgetCapping.Open,
      BudgetCapping.Period,
      BudgetCapping.Monthly,
      BudgetCapping.Overall,
    ]),
    budgetCapping: Yup.number()
      .nullable()
      .transform((value, originalValue) => (isEmptyOptional(originalValue) ? null : value))
      .when('budgetCappingType', {
        is: (type: BudgetCapping | '') => Boolean(type) && type !== BudgetCapping.Open,
        then: (schema) =>
          schema
            .required('commons.required')
            .min(config.min.budgetCapping, numberMinMessage(t, config.min.budgetCapping))
            .max(config.max.budgetCapping, numberMaxMessage(t, config.max.budgetCapping)),
        otherwise: (schema) =>
          schema
            .nullable()
            .min(config.min.budgetCapping, numberMinMessage(t, config.min.budgetCapping))
            .max(config.max.budgetCapping, numberMaxMessage(t, config.max.budgetCapping)),
      }),
    costType: Yup.mixed<CostType | ''>().oneOf(['', CostType.Cpc, CostType.Cpl, CostType.Cpm, CostType.Cpo]),
    costValue: Yup.number()
      .nullable()
      .transform((value, originalValue) => (isEmptyOptional(originalValue) ? null : value))
      .when('costType', {
        is: (type: CostType | '') => Boolean(type),
        then: (schema) => schema.required('commons.required'),
        otherwise: (schema) => schema.nullable(),
      })
      .test('cost-limits', function (value) {
        if (value == null) return true
        const formCostType = (this.parent as BudgetPlanFormValues).costType
        const effectiveCostType = formCostType || currentCostType
        const limits = costLimitsForType(config, effectiveCostType)
        if (limits == null) return true
        if (value < limits.min) {
          return this.createError({ message: numberMinMessage(t, limits.min) })
        }
        if (value > limits.max) {
          return this.createError({ message: numberMaxMessage(t, limits.max) })
        }
        return true
      }),
    startDate: Yup.mixed<string | null>()
      .nullable()
      .test(
        'valid-start-date',
        'commons.required',
        (value) => value == null || value === '' || toUtcDateOnly(value) != null
      ),
    endDate: Yup.mixed<string | null>()
      .nullable()
      .test(
        'valid-end-date',
        'commons.required',
        (value) => value == null || value === '' || toUtcDateOnly(value) != null
      )
      .test('end-after-start', "End date can't be before start date", function (value) {
        const endDate = toUtcDateOnly(value)
        const startDate = toUtcDateOnly((this.parent as BudgetPlanFormValues).startDate)
        if (endDate == null || startDate == null) return true
        return endDate >= startDate
      }),
  })
    .test('at-least-one-change', function (values) {
      if (hasBudgetPlanChange(values as BudgetPlanFormValues)) return true
      return this.createError({
        path: 'atLeastOneChange',
        message: 'adgroup.budgetAndDates.budgetPlans.atLeastOneChange',
      })
    })
    .test('auto-open-forbidden', function (values) {
      if (!(values?.dailyBudgetType === DailyBudgetType.Auto && values?.budgetCappingType === BudgetCapping.Open))
        return true
      return this.createError({
        path: 'autoOpenForbidden',
        message: 'adgroup.budgetAndDates.budgetPlans.autoOpenForbidden',
      })
    }) as Yup.ObjectSchema<BudgetPlanFormValues>
}
